import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  dateFormatter,
  decodeQueryData,
  encoder,
  getSortType,
  logger,
  modalNotification,
  navigateWithParam,
} from "utils";
import { ManageBiographyServices } from "services";
import { dateWithString, nav } from "helpers";
import {
  actionFormatter,
  Breadcrumb,
  checkValidData,
  DataTable,
  ListingHeader,
  ManageBiographiesFilter,
  middleNameFormatter,
  nameImageFormatter,
  PageHeader,
  serialNumberFormatter,
  switchFormatter,
  textFormatter,
} from "../../../../components";

function Biographies() {
  const [biographyList, setBiographyList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noOfPage, setNoOfPage] = useState();
  const [sizePerPage, setSizePerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [param, setParam] = useState({});
  const [searchName, setSearchName] = useState();
  const [visible, setVisible] = useState(false);
  const [page, setPage] = useState(1);
  const [filterData, setFilterData] = useState({});
  const [statusLoading, setStatusLoading] = useState(false);

  const navigate = useNavigate();

  const location = useLocation();

  const { pathname, search } = location;
  const [defaultSort, setDefaultSort] = useState([
    {
      dataField: "",
      order: "",
    },
  ]);

  const getAllBiographies = async () => {
    setLoading(true);
    try {
      let queryParams = {
        offset: (page - 1) * sizePerPage,
        limit: sizePerPage,
        sortBy: param?.sortBy,
        sortType: param?.sortType,
        search: searchName,
        ...filterData,
      };
      const res = await ManageBiographyServices.getAllBioGraphy(queryParams);
      const { data, success } = res;
      if (data && success) {
        setBiographyList(data?.rows);
        setNoOfPage(data?.count > 0 ? Math.ceil(data?.count / sizePerPage) : 1);
        setTotalCount(data?.count);
      }
    } catch (err) {
      logger(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (search) {
      const data = decodeQueryData(search);
      setParam(data);
      setPage(data?.page ?? 1);
      // setSearchName(data?.name ?? "");
      if (data?.sortType) {
        const sortData = [
          {
            order: getSortType(data?.sortType),
            dataField: data?.sortBy,
          },
        ];
        setDefaultSort(sortData);
      } else {
        setDefaultSort({
          dataField: "",
          order: "",
        });
      }
    }
  }, [location]);

  const tableReset = () => {
    setLoading(true);
    setBiographyList([]);
    setNoOfPage(0);
    setTotalCount(0);
  };

  useEffect(() => {
    if (search && JSON.stringify(param) !== "{}") {
      getAllBiographies();
    }
  }, [param]);

  const getSearchValue = (val) => {
    setSearchName(val);
    if (val) {
      tableReset();
    }
  };

  useEffect(() => {
    if (!search) {
      getAllBiographies();
    }
  }, []);

  const onReset = () => {
    setFilterData({});
    tableReset();
    setVisible(false);
    const newParams = { ...param };
    newParams.page = 1;
    navigateWithParam(newParams, navigate, pathname);
  };

  const onSortColumn = (field, order) => {
    const data = { ...param };
    data.sortBy = field;
    data.sortType = order === "asc" ? "ASC" : "DESC";
    navigateWithParam(data, navigate, pathname);
    tableReset();
  };

  const headerSortingClasses = (column, sortOrder) => {
    return sortOrder === "asc" ? "sorting_asc" : "sorting_desc";
  };

  const onChangeStatus = async (val, row) => {
    setStatusLoading(true);
    try {
      let bodyData = {
        isActive: val === true ? 1 : 0,
      };
      const res = await ManageBiographyServices.updateBioGraphyStatusServices(
        row?.id,
        bodyData
      );
      const { message, success } = res;
      if (success) {
        modalNotification({
          type: "success",
          message,
        });
        setBiographyList([]);
        getAllBiographies();
      } else {
        modalNotification({
          type: "error",
          message,
        });
      }
    } catch (error) {
      logger(error);
    }
    setStatusLoading(false);
  };

  const handleFilterSubmit = async (val) => {
    setLoading(true);
    try {
      setFilterData(val);
      tableReset();
      const newParams = { ...param };
      newParams.page = 1;
      navigateWithParam(newParams, navigate, pathname);
      setVisible(false);
      document.body.click();
    } catch (error) {
      logger(error);
    }
    setLoading(false);
  };

  const options = (row) => {
    const optionsArr = [
      {
        name: "View",
        icon: "icon ni ni-eye",
        action: "redirect",
        path: `${nav[row?.themeOnes[0]?.type]}/${encoder(row?.id)}?${encoder(
          row?.themeOnes[0]?.type
        )}`,
        target: "blank",
      },
    ];
    return optionsArr;
  };

  const breadcrumb = [
    {
      path: "/admin/dashboard",
      name: "DASHBOARD",
    },
    {
      path: "#",
      name: "Manage Biographies",
    },
  ];

  const columns = () => {
    let arr = [
      {
        dataField: "id",
        text: "S.No.",
        headerClasses: "w_70",
        headerSortingClasses,
        formatter: (cell, row, index) =>
          serialNumberFormatter(page, sizePerPage, index),
      },
      {
        dataField: "creatorName",
        text: "Creator Name",
        headerClasses: "sorting",
        headerSortingClasses,
        sort: true,
        onSort: (field, order) => {
          onSortColumn(field, order);
        },
        formatter: (cell, row) => {
          const fullName = `${middleNameFormatter(
            textFormatter(row?.user?.firstName),
            textFormatter(row?.user?.middleName),
            textFormatter(row?.user?.lastName),
            true
          )}`;
          return nameImageFormatter(fullName, row?.user?.profileImageUrl);
        },
      },
      {
        dataField: "createdFor",
        text: "Created For",
        headerClasses: "sorting",
        headerSortingClasses,
        sort: true,
        onSort: (field, order) => {
          onSortColumn(field, order);
        },
        formatter: (cell, row) => {
          const fullName = `${middleNameFormatter(
            textFormatter(row?.firstName),
            textFormatter(row?.middleName),
            textFormatter(row?.lastName),
            true
          )}`;
          return row?.themeOnes?.[0]?.type === "theme01"
            ? nameImageFormatter(fullName, row?.themeOnes?.[0]?.profileImageUrl)
            : fullName;
        },
      },
      // {
      //   dataField: "email",
      //   text: "Email Address",
      //   headerClasses: "sorting",
      //   sort: true,
      //   headerSortingClasses,
      //   onSort: (field, order) => {
      //     onSortColumn(field, order);
      //   },
      //   formatter: (cell, row) => textFormatter(row?.user?.email),
      // },
      // {
      //   dataField: "phoneNumber",
      //   text: "Phone Number",
      //   headerClasses: "sorting",
      //   sort: true,
      //   onSort: (field, order) => {
      //     onSortColumn(field, order);
      //   },
      //   headerSortingClasses,
      //   formatter: (cell, row) => textFormatter(row?.user?.phoneNumber),
      // },

      {
        dataField: "address",
        text: "Address",
        headerClasses: "sorting",
        sort: true,
        onSort: (field, order) => {
          onSortColumn(field, order);
        },
        headerSortingClasses,
        formatter: (cell, row) => (
          <> {textFormatter(checkValidData(row?.location))}</>
        ),
      },
      {
        dataField: "createdAt",
        text: "Created On",
        headerClasses: "sorting",
        headerSortingClasses,
        sort: true,
        onSort: (field, order) => {
          onSortColumn(field, order);
        },
        formatter: (cell, row) => dateFormatter(row?.createdAt, dateWithString),
      },
      {
        dataField: "status",
        text: "Publish Status",
        sort: true,
        headerClasses: "sorting",
        onSort: (field, order) => {
          onSortColumn(field, order);
        },
        headerSortingClasses,
      },
      {
        dataField: "permissionStatus",
        text: "Permission",
        sort: true,
        headerClasses: "sorting",
        onSort: (field, order) => {
          onSortColumn(field, order);
        },
        headerSortingClasses,
      },
      {
        dataField: "isActive",
        text: "Status",
        // sort: true,
        // headerClasses: "sorting",
        // onSort: (field, order) => {
        //   onSortColumn(field, order);
        // },
        // headerSortingClasses,
        formatter: (cell, row) =>
          switchFormatter(
            row?.isActive === 1,
            (val) => {
              onChangeStatus(val, row);
            },
            statusLoading,
            row
          ),
      },
      {
        dataField: "action",
        text: "Action",
        headerClasses: "nk-tb-col-tools text-end",
        formatter: (cell, row) => actionFormatter(options(row)),
      },
    ];
    return arr;
  };
  return (
    <>
      <div className="nk-block-head nk-block-head-sm">
        <div className="nk-block-between">
          <PageHeader heading="Biographies">
            <Breadcrumb breadcrumb={breadcrumb} />
          </PageHeader>
          <ListingHeader
            btnArray={["filter"]}
            visible={visible}
            setVisible={setVisible}
            popover={
              <ManageBiographiesFilter
                onReset={onReset}
                filterData={filterData}
                loading={loading}
                onSubmit={handleFilterSubmit}
              />
            }
          />
        </div>
      </div>
      <DataTable
        hasLimit
        noOfPage={noOfPage}
        sizePerPage={sizePerPage}
        page={page}
        param={param}
        count={totalCount}
        tableData={biographyList}
        tableColumns={columns()}
        setSizePerPage={setSizePerPage}
        getSearchValue={getSearchValue}
        defaultSort={defaultSort}
        tableLoader={loading}
        tableReset={tableReset}
      />
    </>
  );
}

export default Biographies;
